/**
 * SOUND-630 — one way to render a company that has no name yet.
 *
 * Self-serve signup created the company before any business name was captured
 * (SOUND-631 closes that going forward by requiring the name at
 * signup, but 15 existing companies predate it, and an internal admin can still
 * create a company without one). Those rows rendered as a blank cell or an
 * em dash, which is indistinguishable from a broken record — so they read as
 * data corruption rather than as what they are: a real signup that has not
 * filled in the application yet. Every one has a working login and a pending
 * application.
 *
 * "New Merchant", italicised, says the record is fine and incomplete. An em dash
 * says something is missing and cannot say what.
 */

export const UNNAMED_COMPANY_LABEL = "New Merchant";

/** True when a company has no usable name. Whitespace-only counts as unnamed. */
export function isUnnamedCompany(company) {
  return !String(company?.name ?? "").trim();
}

/**
 * The company's name for display, or the placeholder.
 *
 * Returns a plain string, so it is safe in a title attribute, a document title,
 * a toast, or an export cell. For the italicised UI treatment use
 * `<CompanyName />`, which wraps this.
 */
export function companyDisplayName(company) {
  return isUnnamedCompany(company) ? UNNAMED_COMPANY_LABEL : company.name.trim();
}

/**
 * The value for a CSV cell.
 *
 * Deliberately EMPTY rather than "New Merchant": an export is data, and writing
 * a placeholder into a spreadsheet would let it be read back as a real business
 * name — the same mistake as exporting an uncapped MID as 0 (SOUND-657).
 */
export function companyNameExportValue(company) {
  return isUnnamedCompany(company) ? "" : company.name.trim();
}
